import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSmartPhraseContext } from '@rsos/components/capstone/base/SmartPhrase';
import { useCurrentPSAP } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import {
  getPreferredMessageByType,
  PreparedResponseType,
  useLazyGetPreconfiguredMessagesQuery,
} from '@rsos/reduxDataStore/src/features/preconfiguredMessages';
import { selectUser, getTokens } from '@rsos/sinatra';
import { interpolateSmartPhrasesWithResolvedPhrases } from '@rsos/utils/SmartPhrases';

export type ResolvedEndingMessage = {
  body: string;
  doNotTranslate: string[];
};

/**
 * Returns an async function that resolves the correct ending message to send
 * when closing a text session. Prefers the authority-configured custom ending
 * message; falls back to the rapidsos system default if none exists. Smart
 * Phrase placeholders are interpolated before returning, and their resolved
 * values are reported as `doNotTranslate` so the backend leaves them intact.
 *
 * Returns `undefined` if no ending message is found or interpolation fails —
 * callers should then omit `endMessage` so the backend uses its hardcoded
 * fallback constant.
 *
 * The preconfigured messages list is pre-fetched by AllMessages in the same
 * conversation view, so this is a cache hit at end-chat time.
 */
export function useEndingMessage({
  orgName,
  callerID,
}: {
  orgName: string;
  callerID: string | undefined;
}): () => Promise<ResolvedEndingMessage | undefined> {
  const [triggerGetMessages] = useLazyGetPreconfiguredMessagesQuery();

  const psap = useCurrentPSAP();
  const user = useSelector(selectUser);
  const token = getTokens()?.token;
  const smartPhraseContext = useSmartPhraseContext({
    psap,
    user,
    orgName,
    token,
    callerID,
  });

  return useCallback(async () => {
    if (!orgName) return undefined;

    const { data: preconfiguredMessages = [] } = await triggerGetMessages({
      org: orgName,
      enabled: true,
    });

    const endingMessage = getPreferredMessageByType(
      preconfiguredMessages,
      PreparedResponseType.ending,
    );

    try {
      const { body, resolvedPhrases } =
        await interpolateSmartPhrasesWithResolvedPhrases(
          endingMessage.body,
          smartPhraseContext,
        );

      return { body, doNotTranslate: Object.values(resolvedPhrases) };
    } catch {
      return undefined;
    }
  }, [triggerGetMessages, orgName, smartPhraseContext]);
}
